<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <b-link class="brand-logo d-none d-md-flex">
        <logo />

        <h2 class="brand-text text-primary ml-1">{{ appName }}</h2>
      </b-link>

      <b-navbar-nav
        class="nav align-items-center ml-auto absolute-locale d-none d-md-flex"
      >
        <locale></locale>
      </b-navbar-nav>

      <b-col lg="8" class="d-none d-lg-flex align-items-center p-5">
        <div
          class="w-100 d-lg-flex align-items-center justify-content-center px-5"
        >
          <b-img fluid :src="imgUrl" />
        </div>
      </b-col>

      <b-col lg="4" class="d-flex align-items-center auth-bg px-2 p-lg-5">
        <b-col sm="8" md="6" lg="12" class="px-xl-2 mx-auto">
          <ul class="no-bullet">
            <locale class="d-md-none"></locale>
          </ul>
          <b-card-title title-tag="h2" class="font-weight-bold mb-1">
            {{ $t("breadcrumb.register") }}
          </b-card-title>
          <b-card-text class="mb-2" v-if="step == 1">
            {{ $t("general.needBasicInformation") }}
          </b-card-text>
          <b-card-text class="mb-2" v-else>
            {{ $t("general.whatKindOfbusiness") }}
          </b-card-text>

          <validation-observer ref="registerForm" #default="{ invalid }">
            <b-form
              class="auth-register-form mt-2"
              @submit.prevent="validationForm"
            >
              <n-input :fields="fields" v-model="data" v-if="step == 1">
                <template #isAgreePolicy="item">
                  <validation-provider
                    #default="{ errors }"
                    :vid="item.field.key"
                    :name="$t(item.field.label)"
                    :rules="item.field.rules"
                  >
                    <b-form-group>
                      <b-form-checkbox
                        v-model="data.isAgreePolicy"
                        name="checkbox-1"
                        :class="errors.length > 0 ? 'is-invalid' : null"
                      >
                        {{ $t("general.iAgree") }}
                        <b-link class="link-underline">
                          {{ $t("general.policyAndTerm") }}
                        </b-link>
                      </b-form-checkbox>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </template>
              </n-input>
              <n-input :fields="businessFields" v-model="data" v-else>
              </n-input>

              <n-button-loading
                type="submit"
                variant="primary"
                :loading="loading"
                :disabled="invalid"
                block
              >
                <span v-if="step == 1">
                  {{ $t("button.signUp") }}
                </span>
                <span v-else>
                  {{ $t("button.submit") }}
                </span>
              </n-button-loading>
            </b-form>
          </validation-observer>

          <p class="text-center mt-2">
            <span>{{ $t("general.alreadyHaveAccount") }}</span>
            <b-link class="link-underline" :to="{ name: 'login' }">
              <span>&nbsp;{{ $t("button.signIn") }}</span>
            </b-link>
          </p>
        </b-col>
      </b-col>
    </b-row>
  </div>
</template>

<script>
/* eslint-disable global-require */
import Logo from "@core/layouts/components/Logo.vue";
import {
  BRow,
  BCol,
  BLink,
  BButton,
  BForm,
  BFormGroup,
  BFormCheckbox,
  BImg,
  BCardTitle,
  BCardText,
  BNavbarNav,
} from "bootstrap-vue";
import store from "@/store/index";
import { $themeConfig } from "@themeConfig";
import FormInput from "./registerBasicFormInput";
import BusinessFormInput from "./registerBusinessFormInput";
import NInput from "@/components/NInput";
import NButtonLoading from "@/components/NButtonLoading";
import Locale from "@/layouts/components/app-navbar/Locale.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import useJwt from "@/auth/jwt/useJwt";
import { getHomeRouteForLoggedInUser } from "@/auth/utils";
import { initialAbility } from "@/libs/acl/config";
import Repository from "@/repositories/RepositoryFactory";
import { mapAbility } from "@/libs/helper";

const RegisterRepository = Repository.get("register");

export default {
  components: {
    BRow,
    BImg,
    BCol,
    BLink,
    BButton,
    BForm,
    BFormGroup,
    BCardText,
    BCardTitle,
    BFormCheckbox,
    BNavbarNav,
    Logo,
    Locale,
    NInput,
    NButtonLoading,
  },
  data() {
    return {
      data: {
        firstName: null,
        lastName: null,
        comanyName: null,
        phone: null,
        email: null,
        password: null,
        retypePassword: null,
        isAgreePolicy: false,
        businessTypeId: null,
      },
      step: 1,
      loading: false,
      sideImg: require("@/assets/images/pages/register-v2.svg"),
    };
  },
  computed: {
    imgUrl() {
      if (store.state.appConfig.layout.skin === "dark") {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require("@/assets/images/pages/register-v2-dark.svg");
        return this.sideImg;
      }
      return this.sideImg;
    },
  },
  methods: {
    validationForm() {
      this.$refs.registerForm.validate().then((success) => {
        if (success) {
          if (this.step == 1) {
            this.checkUnique();
          } else {
            this.submit();
          }
        }
      });
    },
    checkUnique() {
      this.loading = true;
      RegisterRepository.checkUnique({ ...this.data })
        .then((response) => {
          const data = response?.data?.data;
          if (!data?.isExistEmail && !data?.isExistPhone) {
            this.step = 2;
            return;
          }
          if (data?.isExistEmail) {
            this.$refs.registerForm.setErrors({
              email: this.$t("validations.messages.unique", {
                _field_: this.$t("field.email"),
              }),
            });
          }
          if (data?.isExistPhone) {
            this.$refs.registerForm.setErrors({
              phone: this.$t("validations.messages.unique", {
                _field_: this.$t("field.phoneNumber"),
              }),
            });
          }
        })
        .catch((error) => {})
        .then(() => {
          this.loading = false;
        });
    },
    submit() {
      this.loading = true;

      RegisterRepository.register({
        ...this.data,
        origin: Intl
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : "Asia/Phnom_Penh",
      })
        .then((response) => {
          const data = response?.data?.data;

          const userData = data.user || {};
          useJwt.setToken(data.accessToken);
          useJwt.setRefreshToken(data.refreshToken);
          localStorage.setItem(useJwt.jwtConfig.storageUserDataKeyName, JSON.stringify(userData));
          this.$store.commit("profile/UPDATE_PROFILE", userData);

          let newAbility = [...initialAbility];
          if (userData?.ability) {
            newAbility = mapAbility(userData, newAbility, initialAbility);
          }
          this.$ability.update(newAbility);
          this.$router
            .replace(getHomeRouteForLoggedInUser("admin", null, newAbility))
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                position: "top-right",
                props: {
                  title: this.$t("general.welcomeUser", {
                    name: this.data.firstName,
                  }),
                  icon: "CoffeeIcon",
                  variant: "success",
                  text: this.$t("alert.successRegistered"),
                },
              });
            });
        })
        .catch((error) => {
          if (error?.response?.status == 422) {
            const validate = error.response?.data?.validate;
            if (validate) {
              if (validate.email) {
                this.step = 1;
              }
              this.$refs.registerForm.setErrors(error.response?.data?.validate);
            }
          }
        })
        .then(() => {
          this.loading = false;
        });
    },
  },
  setup() {
    const { appName } = $themeConfig.app;
    const fields = FormInput;
    const businessFields = BusinessFormInput;
    return {
      appName,
      fields,
      businessFields,
    };
  },
};
/* eslint-disable global-require */
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
</style>
